'use client';
/**
 * 动态获取商品数据
 * 用于在组件挂载时获取商品数据，支持预览模式和正常模式
 * @param params
 * @returns 动态获取商品数据的函数
 * @template T 商品数据类型
 * @param params.componentId 组件ID
 * @param params.componentBlockId 组件BlockID
 * @param params.currencyName 货币名称
 * @param params.enableAuthRefetch 是否启用认证刷新
 * @param params.dynamicProductsData 商品数据
 * @param params.hasAttemptedFetch 是否已尝试过获取数据
 * @param params.isLoading 是否正在加载数据
 * @param params.setDynamicProductsData 设置商品数据的函数
 * @param params.setIsLoading 设置加载状态的函数
 * @param params.setHasAttemptedFetch 设置已尝试过获取数据的函数
 * @returns 动态获取商品数据的函数
 */

import { getComponentProductData } from '@/api/home';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { useCallback, useEffect, useRef } from 'react';

interface UseFetchDynamicProductsParams<T> {
  componentId?: string;
  componentBlockId?: string | number;
  currencyName?: string;
  enableAuthRefetch?: boolean;
  dynamicProductsData: T | null;
  hasAttemptedFetch: boolean;
  isLoading: boolean;
  setDynamicProductsData: React.Dispatch<React.SetStateAction<T | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setHasAttemptedFetch: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useFetchDynamicProducts<T>({
  componentId,
  componentBlockId,
  currencyName,
  enableAuthRefetch = true,
  dynamicProductsData,
  hasAttemptedFetch,
  isLoading,
  setDynamicProductsData,
  setIsLoading,
  setHasAttemptedFetch,
}: UseFetchDynamicProductsParams<T>) {
  const token = useStore((state) => state.token);
  const isLoggedIn = !!token;
  const authStatusRef = useRef(isLoggedIn);

  const fetchDynamicProducts = useCallback(async () => {
    if (!componentId || !componentBlockId) {
      return;
    }

    const isPreview =
      typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);

    setIsLoading(true);
    try {
      const res: any = await getComponentProductData({
        component_id: componentId,
        id: Number(componentBlockId),
        currency: currencyName || 'USD',
        status: isPreview ? 2 : 1,
      });

      if (res && typeof res !== 'string' && res.data) {
        setDynamicProductsData(res.data);
      } else {
        setDynamicProductsData(null);
      }
    } catch (err) {
      logger.error('获取商品数据失败:', err);
    } finally {
      setIsLoading(false);
      setHasAttemptedFetch(true);
    }
  }, [
    componentBlockId,
    componentId,
    currencyName,
    setDynamicProductsData,
    setHasAttemptedFetch,
    setIsLoading,
  ]);

  useEffect(() => {
    if (authStatusRef.current === isLoggedIn) {
      return;
    }

    authStatusRef.current = isLoggedIn;

    if (!enableAuthRefetch) {
      return;
    }

    if (!hasAttemptedFetch || !dynamicProductsData || isLoading) {
      return;
    }

    fetchDynamicProducts();
  }, [
    dynamicProductsData,
    enableAuthRefetch,
    fetchDynamicProducts,
    hasAttemptedFetch,
    isLoading,
    isLoggedIn,
  ]);

  return fetchDynamicProducts;
}
